import React from "react";
import { Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Paragraph } = Typography;

const introParaGraphStyle = { lineHeight: "1.5em", textAlign: "justify" };

export const ICD11Intro = (
  <React.Fragment>
    <Paragraph style={introParaGraphStyle}>
      Many countries across the world are looking at the feasibility of transitioning to the 11th revision of the
      International Classification of Diseases (ICD-11). This calculator has been created with the intention of making
      it easy to determine the cost that can be expected to make the transition from ICD-10 to ICD-11 and the possible
      benefits that may be realised through using the ICD-11 coding system.
    </Paragraph>
    <Paragraph style={introParaGraphStyle}>
      To begin using the calculator, simply switch through the tabs and fill in the forms with accurate information.
      Some fields are required (indicated by a *) in order to provide a cost estimation, while others are recommended.
      It is recommended to fill as many fields as possible to receive a cost estimation. Many of the fields have tips to
      provide a more detailed explanation. Hover over {<InfoCircleOutlined style={{ color: "#d6001c" }} />} to show the
      explanation. The final cost estimation can be seen on the final tab. The pie charts are dependent on the values
      entered in the preceding tabs. If no value is entered, pie charts and other visualizations will not appear. Please
      enter some values in all cost tabs to visualize the cost pie chart on the summary page. Similarly, please enter
      some values to visualize the benefits pie chart.
    </Paragraph>
    <Paragraph style={introParaGraphStyle}>
      The buttons in the top left corner allows for currency selection, the option to download or upload data and the
      option to quickly toggle between tabs. Downloading the data provides an easy method to share and save the cost
      estimation, and enables uploading the data into the calculator for future calculations. Uploading a previously
      downloaded file will fill out the forms, allowing for modifications to a previous estimate to be easily altered.
    </Paragraph>
    <Paragraph style={introParaGraphStyle}>
      {" "}
      <a target="_blank" href={process.env.PUBLIC_URL + "/DataCollectionRecommendations.pdf"}>
        Here
      </a>{" "}
      is a document outlining all the data which is recommended to be collected before using the calculator. Data may be
      collected at the hospital, state, provincial, or country level. The cost calculated will be based on the level at
      which data is collected. For eg., if the data entered in the calculator has been collected from a hospital, cost
      and benefit would be estimated for that specific hospital.
    </Paragraph>
    <Paragraph style={introParaGraphStyle}>
      {" "}
      <a target="_blank" href={process.env.PUBLIC_URL + "/ICD11CostBenefitCalculatorScreenshots.pdf"}>
        Here
      </a>{" "}
      is a sample PDF which demonstrates how the numbers would be entered. It also demonstrates visualizations which
      would appear when all data fields have been filled. Please note, the numbers shown in this PDF are arbitrary
      numbers, provided just as an example.
    </Paragraph>
    <Paragraph style={introParaGraphStyle}>References:</Paragraph>
    <Paragraph style={introParaGraphStyle}>
      <ol>
        <li>
          Khair S, Quan H, Eastwood CA, Doktorchik C, Zhao J, Losev T, et al. Paving the Way: A Cost and Outcome
          Evaluation Framework for the Transition from the 10th to the 11th Version of the International Classification
          of Disease. Perspectives in Heal Information Management. 2020; Available from:{" "}
          <a href="https://perspectives.ahima.org/paving-the-way-a-cost-and-outcome-evaluation-framework-for-the-transition-from-the-10th-to-the-11th-version-of-the-international-classification-of-disease/">
            https://perspectives.ahima.org/paving-the-way-a-cost-and-outcome-evaluation-framework-for-the-transition-from-the-10th-to-the-11th-version-of-the-international-classification-of-disease/
          </a>
        </li>
        <li>
          Khair S, Doktorchik C, Eastwood CA, Quan H, Chen O, et al. The International Classification of Diseases, 11th
          Revision- A Cost Estimate and a Benefit Discussion. (Manuscript Submitted for Publication).
        </li>
        <li>
          Eastwood CA, Southern DA, Khair S, Doktorchik C, Cullen D, Ghali WA, et al. Field testing a new ICD coding
          system: methods and early experiences with ICD-11 Beta Version 2018. BMC Res Notes [Internet]. 2022;15(1):1–7.
          Available from:{" "}
          <a href="https://doi.org/10.1186/s13104-022-06238-2">https://doi.org/10.1186/s13104-022-06238-2</a>
        </li>
        <li>
          Eastwood CA, Southern DA, Doktorchik C, Khair S, Cullen D, Boxill A, et al. Training and Experience of Coding
          with WHO’s International Classification of Diseases 11th Version (ICD-11). Heal Inf Manag J. 2021. Available
          from:{" "} <a href="https://doi.org/10.1177/18333583211038633">https://doi.org/10.1177/18333583211038633</a>
        </li>
      </ol>
    </Paragraph>
  </React.Fragment>
);

export const benefitsExplanation = (
  <Paragraph>
    Countries can insert their estimated savings amounts related to their identified benefits into the calculator.
    Countries can calculate benefits according to their own implementation timeline. It is recommended to use the same
    number of years for calculating future benefits and future costs, to allow for accurate comparison.
  </Paragraph>
);

export const summaryExplanation = (
  <Paragraph>
    The pie charts provide a visual breakdown of the data entered in the previous tabs. To see a detailed breakdown for
    a cost category, click on the corresponding pie slice in the Cost Breakdown pie chart. Hover over individual slices
    to see the exact value of each slice. The break in period to be displayed can be selected, and a pie chart for the
    benefits breakdown can be generated.
  </Paragraph>
);

export const inflationTooltip = (
  <p style={{ marginBottom: 0 }}>
    Please enter the current inflation rate. Follow this link to find the inflation rate for your country:{" "}
    <a href="https://data.worldbank.org/indicator/FP.CPI.TOTL.ZG" rel="noopener noreferrer" target="_blank">
      https://data.worldbank.org/indicator/FP.CPI.TOTL.ZG
    </a>
    .
  </p>
);

export const yearsIntoFutureTooltip =
  "Please calculate which year the ICD-11 system will be implemented. For example: If the cost estimate is being completed in the year 2020 and the system will be implemented in the year 2023, the 'years into the future' value would be 3.";

export const oneTimeSoftwareTooltip =
  "Cost for installing new software to be able to handle the ICD-11 system and its features. In Canada, the two main software vendors are 3M and MED2020.";

export const softwareAnnualFeeTooltip = "The annual fee required for updating and maintaining the software.";

export const icd11codingTimeInitialTooltip =
  "Please enter your predicted initial coding time per inpatient chart using ICD-11. If you are unsure, our study team conducted a field trial that trained coders to code using ICD-11, and found that initial coding time was an average of 13 minutes per chart. Please see the links to the published papers on the homepage for more details.";

export const icd11codingTimeFinalTooltip =
  "Please enter your predicted final coding time per inpatient chart using ICD-11. If you are unsure, our study team conducted a field trial that trained coders to code using ICD-11, and found that final coding time was an average of 10 minutes per chart. As coders became more adept at coding, the number of minutes to code a chart decreased. Please see the links to the published papers on the homepage for more details.";

export const breakInPeriodTooltip =
  "Break-in period refers to the number of months it would take a hospital to return to previous levels of productivity. Please refer to our papers for further intuition and explanation.";

export const workingDaysPerMonthTooltip = "Please enter the number of working days appropriate for your hospital.";

export const hourlyBenefitTooltip =
  "Hourly benefit paid to staff which is in excess of/on top of their salary. This could include flexible spending or medical expenses. Benefit amounts can be different for different staff depending on their 'level' of employment.";

export const retrainingHoursTooltip =
  "Number of hours it will take to re-train a staff member to use the ICD-11 coding system.";

export const otherSystemCosts =
  "For example, other system costs may include expenses such as updates and replacements to electronics and computer hardware.";

export const otherRetrainCosts =
  "For example, other costs may include expenses such as provision of lunch during retraining, facility fees, and the cost of accommodation for both students and staff.";

export const simpleBoxTooltip = "Placeholder tooltip";

export const autoCodingTooltip =
  "There may be a need for fewer coders due to quicker and automated coding systems. This may reduce the costs incurred to train coders.";

export const vendorCostSavingsTooltip =
  "The ICD-11 coding system includes a coding tool, which is a code searching tool that may eliminate the need for countries to purchase external code search software (especially countries not already engaged with data abstraction/code assistance software). This might reduce the cost of obtaining the coding software and licensing fees.";

export const travelSavingTooltip =
  "The advent of online learning platforms due to the COVID-19 pandemic has made it more likely that coder training will be online. This will result in significant cost savings from travel, accommodation, and facilities. Moreover, the inclusion of the ICD-11 coding tool would mean that the coding guidebooks would no longer be purchased.";

export const accurateBillingSavingTooltip =
  "Certain newer procedure codes may not be found in the ICD-10- CA coding system. ICD-11 includes newer procedures, enhanced details and will allow for the exact procedures to be coded, instead of being coded/ lumped under a different but similar procedure. This would lead to a differentiation between complex and simple procedures, resulting in accurate documentation and billing.";

export const reducedClaimsSavingsTooltip =
  "A reduction in miscoded claims, due to the more detailed and specific ICD-11 codes, would result in savings. Correcting miscoded and unspecified documentation is reduced due to specific and clear categorization of codes in ICD-11, resulting in less administrative work, time and in turn, money.";

export const otherSavingsTooltip =
  "Other benefits may arise from having a record of specific and detailed procedures being coded. This may aid physicians to understand and create better disease management, as well as help researchers with accurate statistics on specific procedures. Moreover, Khair et. al (2020) outlined benefit measures focusing on the quantity and quality of ICD-11 codes. ";

export const inflationCalcTooltip =
  "A forward flat inflation calculation method is used to adjust the costs for inflation. You may use inflation forecasting tools for a more accurate inflation adjustment. ";

export const finalMinimumCostTooltip =
  "Inflation Adjusted Total Cost Estimation (Minimum Break-in Period) - Total Benefits";

export const finalMaximumCostTooltip =
  "Inflation Adjusted Total Cost Estimation (Maximum Break-in Period) - Total Benefits";
